// Dotted field names address values INSIDE a row's JSON columns: a custom field is
// `custom_fields.<key>`, so forms, lists and details read and write it through the row's
// `custom_fields` object. These are the only two helpers that know that.

export function getPath(row: Record<string, unknown> | undefined | null, name: string): unknown {
  if (!row) return undefined;
  if (!name.includes('.')) return row[name];
  let cur: unknown = row;
  for (const part of name.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

/** Set `name` (dotted or plain) on `target`, creating intermediate objects. Mutates and returns `target`. */
export function setPath(target: Record<string, unknown>, name: string, value: unknown): Record<string, unknown> {
  if (!name.includes('.')) {
    target[name] = value;
    return target;
  }
  const parts = name.split('.');
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i]!;
    const next = cur[p];
    if (next == null || typeof next !== 'object' || Array.isArray(next)) cur[p] = {};
    cur = cur[p] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
  return target;
}

/** Field names that live inside a JSON column (`custom_fields.colour` → 'custom_fields'). */
export function pathRoot(name: string): string {
  const i = name.indexOf('.');
  return i < 0 ? name : name.slice(0, i);
}

/** Remove `name` (dotted or plain) from `target`; empty intermediate objects are removed too. */
export function deletePath(target: Record<string, unknown>, name: string): void {
  if (!name.includes('.')) {
    delete target[name];
    return;
  }
  const parts = name.split('.');
  const parents: Record<string, unknown>[] = [target];
  let cur: Record<string, unknown> = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const next = cur[parts[i]!];
    if (next == null || typeof next !== 'object') return;
    cur = next as Record<string, unknown>;
    parents.push(cur);
  }
  delete cur[parts[parts.length - 1]!];
  for (let i = parents.length - 1; i > 0; i--) {
    if (Object.keys(parents[i]!).length === 0) delete parents[i - 1]![parts[i - 1]!];
    else break;
  }
}
